/**
 * Dati dei grafici del Trend (V62): voti, eventi e attributi del giocatore
 * della scheda nel tempo, sulle stagioni scelte. I tipi rispecchiano
 * `apps/api/src/servizi/trend.ts`.
 *
 * Ogni sezione è un grafico solo, con una serie per valore: i voti di partite
 * e allenamenti, gli eventi scelti con i minuti in barre (se accesi), gli attributi
 * scelti. I punti stanno sul giorno: due voti o due misurazioni dello stesso
 * giorno fanno la media, due partite lo stesso giorno sommano gli eventi.
 * Con il progressivo eventi e minuti si accumulano partita dopo partita.
 */

import type { FamigliaAttributo } from '@calcio/shared';
import { giornoUtc } from '../comuni/date.js';

export interface PartitaTrend {
  partitaId: string;
  data: string;
  competizioneId: string;
  minuti: number;
  gol: number;
  autogol: number;
  assist: number;
  tiriSpecchio: number;
  tiriFuori: number;
  passaggiCompletati: number;
  passaggiSbagliati: number;
  palleConquistate: number;
  pallePerse: number;
  gialli: number;
  rossi: number;
  /** Assente per chi non legge i voti. */
  voto?: number | null;
}

export interface GiocatoreTrend {
  id: string;
  nome: string;
  cognome: string;
  partite: PartitaTrend[];
  votiAllenamento?: { data: string; valore: number }[];
  misurazioni?: { attributoId: string; data: string; valore: number }[];
}

export interface AttributoTrend {
  id: string;
  nome: string;
  famiglia: FamigliaAttributo;
}

export interface Trend {
  /** Tutte le stagioni della società, dalla più recente. */
  stagioni: { id: string; nome: string; corrente: boolean }[];
  /** Le stagioni mostrate; senza scelta, la corrente. */
  scelte: string[];
  competizioni: { id: string; nome: string }[];
  attributi?: AttributoTrend[];
  giocatore: GiocatoreTrend;
}

export type Evento =
  | 'minuti'
  | 'gol'
  | 'assist'
  | 'tiri'
  | 'tiriSpecchio'
  | 'tiriFuori'
  | 'passaggi'
  | 'passaggiCompletati'
  | 'passaggiSbagliati'
  | 'palleConquistate'
  | 'pallePerse'
  | 'autogol'
  | 'gialli'
  | 'rossi';

/** Gli eventi delle linee; i minuti, barre da accendere o spegnere, stanno a parte. */
export const EVENTI_TREND: { chiave: Exclude<Evento, 'minuti'>; etichetta: string }[] = [
  { chiave: 'gol', etichetta: 'Gol' },
  { chiave: 'assist', etichetta: 'Assist' },
  { chiave: 'tiri', etichetta: 'Tiri' },
  { chiave: 'tiriSpecchio', etichetta: 'Tiri in porta' },
  { chiave: 'tiriFuori', etichetta: 'Tiri fuori' },
  { chiave: 'passaggi', etichetta: 'Passaggi' },
  { chiave: 'passaggiCompletati', etichetta: 'Passaggi riusciti' },
  { chiave: 'passaggiSbagliati', etichetta: 'Passaggi sbagliati' },
  { chiave: 'palleConquistate', etichetta: 'Palle recuperate' },
  { chiave: 'pallePerse', etichetta: 'Palle perse' },
  { chiave: 'autogol', etichetta: 'Autogol' },
  { chiave: 'gialli', etichetta: 'Gialli' },
  { chiave: 'rossi', etichetta: 'Rossi' },
];

/** Senza scelta nella query string, i passaggi. */
export function eventiDaQuery(valore: string | null): Exclude<Evento, 'minuti'>[] {
  if (valore == null) return ['passaggi'];
  const scelti = new Set(valore.split(','));
  return EVENTI_TREND.map((e) => e.chiave).filter((c) => scelti.has(c));
}

/**
 * Gli attributi con almeno una misurazione nelle stagioni scelte: gli altri
 * non avrebbero una linea. Nell'ordine del catalogo.
 */
export function attributiMisurati(dati: Trend): AttributoTrend[] {
  const misurati = new Set((dati.giocatore.misurazioni ?? []).map((m) => m.attributoId));
  return (dati.attributi ?? []).filter((a) => misurati.has(a.id));
}

/**
 * Gli attributi scelti, nell'ordine di scelta (che dà il colore), fra i
 * misurati. Senza scelta nella query string, quelli della prima famiglia
 * che ne ha.
 */
export function attributiDaQuery(
  valore: string | null,
  misurati: readonly AttributoTrend[],
): string[] {
  if (valore == null) {
    const prima = misurati[0]?.famiglia;
    return misurati.filter((a) => a.famiglia === prima).map((a) => a.id);
  }
  const ids = new Set(misurati.map((a) => a.id));
  return valore.split(',').filter((id) => ids.has(id));
}

export interface Punto {
  data: string;
  valore: number;
}

/** Il valore di cui disegnare i punti: un voto, un evento o un attributo. */
export type Fonte =
  | { tipo: 'voto' }
  | { tipo: 'votoAllenamento' }
  | { tipo: 'evento'; evento: Evento }
  | { tipo: 'attributo'; id: string };

function valoreEvento(p: PartitaTrend, e: Evento): number {
  if (e === 'tiri') return p.tiriSpecchio + p.tiriFuori;
  if (e === 'passaggi') return p.passaggiCompletati + p.passaggiSbagliati;
  return p[e];
}

/** I punti grezzi di una fonte, prima di raggrupparli per giorno. */
function puntiGrezzi(g: GiocatoreTrend, fonte: Fonte, competizione?: string): Punto[] {
  const partite = competizione
    ? g.partite.filter((p) => p.competizioneId === competizione)
    : g.partite;
  switch (fonte.tipo) {
    case 'voto':
      return partite.flatMap((p) => (p.voto == null ? [] : [{ data: p.data, valore: p.voto }]));
    case 'votoAllenamento':
      // Gli allenamenti non hanno competizione: con un filtro non compaiono.
      return competizione ? [] : (g.votiAllenamento ?? []);
    case 'attributo':
      return (g.misurazioni ?? [])
        .filter((m) => m.attributoId === fonte.id)
        .map((m) => ({ data: m.data, valore: m.valore }));
    case 'evento':
      return partite.map((p) => ({ data: p.data, valore: valoreEvento(p, fonte.evento) }));
  }
}

/** I punti di una fonte, uno per giorno, in ordine di data. */
export function puntiDi(
  g: GiocatoreTrend,
  fonte: Fonte,
  { competizione, progressivo = false }: { competizione?: string; progressivo?: boolean } = {},
): Punto[] {
  const perGiorno = new Map<string, number[]>();
  for (const p of puntiGrezzi(g, fonte, competizione)) {
    perGiorno.set(p.data, [...(perGiorno.get(p.data) ?? []), p.valore]);
  }
  const conteggio = fonte.tipo === 'evento';
  const somma = (v: number[]) => v.reduce((a, b) => a + b, 0);
  let totale = 0;
  return [...perGiorno]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, valori]) => {
      if (!conteggio) {
        return { data, valore: Math.round((somma(valori) / valori.length) * 100) / 100 };
      }
      totale += somma(valori);
      return { data, valore: progressivo ? totale : somma(valori) };
    });
}

/** Il giorno come istante UTC, per ordinare le righe senza sentire i fusi. */
export const istante = (data: string): number => giornoUtc(data).getTime();

export type RigaTrend = { t: number; data: string } & Record<string, number | string | null>;

/**
 * Una riga per giorno in cui almeno una serie ha un punto, un valore per
 * serie: la serie che quel giorno non ha punto resta vuota e la linea la
 * scavalca.
 */
export function righeTrend(
  serie: readonly { chiave: string; fonte: Fonte }[],
  g: GiocatoreTrend,
  opzioni: { competizione?: string; progressivo?: boolean } = {},
): RigaTrend[] {
  const righe = new Map<string, RigaTrend>();
  for (const s of serie) {
    for (const p of puntiDi(g, s.fonte, opzioni)) {
      const riga =
        righe.get(p.data) ??
        ({
          t: istante(p.data),
          data: p.data,
          ...Object.fromEntries(serie.map((x) => [x.chiave, null])),
        } as RigaTrend);
      riga[s.chiave] = p.valore;
      righe.set(p.data, riga);
    }
  }
  return [...righe.values()].sort((a, b) => a.t - b.t);
}
